import { TextArea as BlueprintTextArea } from '@blueprintjs/core';
import { ChevronRight, Information } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Heading,
  Icon,
  Input,
  Textarea,
  Tooltip,
} from 'vet-bones/design-system/components';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { TEST_IDS } from 'src/constants/testIds';
import { useCreateProjectBase } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectBase';
import {
  CreateProjectStepValue,
  useCreateProjectState,
} from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';

export const ProjectScopingPanel: React.VFC = () => {
  const {
    name,
    description,
    didGenerateInitialQuestions,
    handleGenerateQuestions,
    setCurrentStep,
    setName,
    setDescription,
  } = useCreateProjectState();
  const createProject = useCreateProjectBase();
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const textAreaRef = React.useRef<BlueprintTextArea>(null);
  React.useEffect(() => {
    if (!description && textAreaRef.current?.textareaElement) {
      textAreaRef.current.setState({ height: undefined });
    }
  }, [description, textAreaRef]);

  return (
    <div className="sd-project-scoping-panel">
      <div className="sd-project-scoping-panel-header">
        <div className="sd-create-project-panel-header-text">
          <Heading size="2xl">
            {copy.components.createProjectOverlay.projectScopingPanel.title}
            <span>
              {
                copy.components.createProjectOverlay.projectScopingPanel
                  .titleSpan
              }
            </span>
          </Heading>
          <Body size="lg">
            {copy.components.createProjectOverlay.projectScopingPanel.subtitle}
          </Body>
        </div>

        <div className="sd-project-scoping-panel-header-help">
          <Icon IconComponent={Information} size="md" />
          <Body size="md">
            {copy.components.createProjectOverlay.projectScopingPanel.helpText}
          </Body>
        </div>
      </div>

      <div className="sd-project-scoping-panel-title">
        <Body size="xl">
          {copy.components.createProjectOverlay.projectScopingPanel.titleLabel}
        </Body>
        <Input
          autoFocus
          className="sd-project-scoping-panel-title-input"
          clearable={false}
          id="project-name"
          label=""
          minimal
          onChange={(evt) => {
            setName(evt.target.value);
          }}
          onKeyDown={(evt) => {
            if (name.trim() && evt.key === 'Enter') {
              inputRef.current?.focus();
            }
          }}
          placeholder={
            copy.components.createProjectOverlay.projectScopingPanel
              .namePlaceholder
          }
          size="lg-inside"
          testId={TEST_IDS.createProjectOverlay.projectScopingPanel.nameInput}
          value={name}
        />
      </div>

      <div className="sd-project-scoping-panel-description">
        <Body size="xl">
          {
            copy.components.createProjectOverlay.projectScopingPanel
              .descriptionLabel
          }
        </Body>
        <Textarea
          className="sd-project-scoping-panel-description-textarea"
          growVertically
          id="project-description"
          inputRef={inputRef}
          label=""
          minimal
          onChange={(evt) => {
            setDescription(evt.target.value);
          }}
          placeholder={
            copy.components.createProjectOverlay.projectScopingPanel
              .descriptionPlaceholder
          }
          testId={
            TEST_IDS.createProjectOverlay.projectScopingPanel
              .descriptionTextarea
          }
          textAreaRef={textAreaRef}
          value={description}
        />
      </div>

      <div className="sd-create-project-panel-footer">
        <div className="sd-create-project-panel-footer-actions" />
        <div className="sd-create-project-panel-footer-actions">
          <Tooltip
            description={
              copy.components.createProjectOverlay.projectScopingPanel
                .titleRequiredTooltip
            }
            disabled={Boolean(name.trim())}
          >
            <Button
              disabled={!name.trim()}
              onClick={() => {
                if (useVoiceboxSuggestions) {
                  if (!didGenerateInitialQuestions) {
                    handleGenerateQuestions();
                  }
                  setCurrentStep(CreateProjectStepValue.COMPETENCY_QUESTIONS);
                } else {
                  createProject();
                }
              }}
              rightIcon={ChevronRight}
              size="lg"
              testId={
                useVoiceboxSuggestions
                  ? TEST_IDS.createProjectOverlay.projectScopingPanel.nextButton
                  : TEST_IDS.createProjectOverlay.projectScopingPanel
                      .submitButton
              }
              variant="primary-accent"
            >
              {useVoiceboxSuggestions
                ? copy.components.createProjectOverlay.steps.competencyQuestions
                : copy.components.createProjectOverlay.steps.createProject}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
